// ISET intake form configuration

export const eligibilityStep = {
  stepId: "eligibility",
  title: "Eligibility",
  description: "Please answer the following questions to determine your eligibility for the ISET program.",
  questions: [
    {
      id: "eligibility_is_indigenous",
      label: "Are you a member of a First Nations, Inuit, or Métis group?",
      hint: "This program is for Indigenous Peoples: First Nations, Inuit, or Métis.",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "eligibility_gender",
      label: "Do you identify as a woman, trans woman, Two-Spirit, or gender-diverse individual who identifies as a woman?",
      hint: "ISET is open to Indigenous women, trans women, Two-Spirit, and gender-diverse people who identify as women.",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "eligibility_citizenship",
      label: "Are you a Canadian citizen?",
      hint: "You must be a Canadian citizen to qualify for ISET funding.",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "eligibility_age",
      label: "Are you 15 years of age or older?",
      hint: "Applicants must be at least 15 years old at the time of application.",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "eligibility_employment_status",
      label: "Are you unemployed, under-employed, or at risk of unemployment?",
      hint: "You may qualify if you are not working, working less than you want, or at risk of losing your job.",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "eligibility_pursuing_training",
      label: "Are you pursuing or planning to pursue post-secondary training leading to employment?",
      hint: "This includes college, university, or other recognized training programs that help you get a job.",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "eligibility_funding_gap",
      label: "Do you have a financial need not fully covered by other funding?",
      hint: "ISET funding is for applicants whose education or training costs are not fully paid by other sources (e.g., Band, student loans).",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    },
    {
      id: "eligibility_previous_default",
      label: "Have you ever been disqualified from ISET funding due to default or other reasons?",
      hint: "If you have previously lost ISET funding due to default or other disqualification, you may not be eligible.",
      type: "radio",
      required: true,
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
      ]
    }
  ],
  stopCondition: {
    message: "Based on your answers, you are not eligible for the ISET program. If you believe this is an error, please contact us for more information."
  }
};

export const isetFormConfig = [
  eligibilityStep,
  {
    stepId: "personal_details_sin",
    title: "What is your Social Insurance Number (SIN)?",
    description: "We need your Social Insurance Number to process your request. It will only be used to confirm your eligibility and won’t be shared without your permission",
    questions: [
      {
        id: "sin_number",
        type: "text",
        label: "Social Insurance Number (SIN)",
        hint: "Your 9-digit Social Insurance Number (e.g., 123 456 789)",
        required: true,
        validation: {
          pattern: "^\\d{9}$",
          errorMessage: "Enter a valid 9-digit SIN (numbers only)."
        }
      }
    ]
  },
  {
    stepId: "personal_details_name",
    title: "What is your name?",
    description: "Enter your full legal name.  This should match the name on your identification documents.",
    questions: [
      {
        id: "title",
        type: "radio",
        label: "Title (optional)",
        options: [
          { value: "ms", label: "Ms." },
          { value: "mrs", label: "Mrs." },
          { value: "miss", label: "Miss" },
          { value: "mx", label: "Mx." }
        ],
        required: false
      },
      {
        id: "last_name",
        type: "text",
        label: "Last Name",
        required: true
      },
      {
        id: "first_name",
        type: "text",
        label: "First Name",
        required: true
      },
      {
        id: "middle_names",
        type: "text",
        label: "Middle Name(s) or Initials (optional)",
        required: false
      },
      {
        id: "preferred_name",
        type: "text",
        label: "Preferred Name (if different) (optional)",
        required: false
      }
    ]
  },
  {
    stepId: "personal_details_dob",
    title: "What is your date of birth?",
    questions: [
      {
        id: "date_of_birth",
        type: "date_parts",
        hint: "For example, 15 5 1990",
        required: true,
        validation: {
          errorMessage: "Enter a valid date of birth."
        }
      }
    ]
  },
  {
    stepId: "personal_details_gender",
    title: "What is your gender identity?",
    questions: [
      {
        id: "gender",
        type: "radio",
        hint: "This question helps us ensure that ISET programming is reaching its intended audience. The program is open to Indigenous women, Two-Spirit, transgender, and gender-diverse people who identify as women. You may answer in the way that best reflects your gender identity.",
        options: [
          { value: "woman", label: "Woman" },
          { value: "two_spirit", label: "Two-Spirit" },
          { value: "trans_woman", label: "Transgender woman" },
          { value: "gender_diverse", label: "Gender-diverse" },
          { value: "prefer_not_to_say", label: "Prefer not to say" }
        ],
        required: true
      }
    ]
  },
  {
    stepId: "contact_information",
    title: "Contact Information",
    description: "Provide your contact details so we can reach you during the application process.",
    questions: [
      {
        id: "street_address",
        type: "text",
        label: "Street Address",
        hint: "Include apartment or unit number if applicable.",
        required: true
      },
      {
        id: "city",
        type: "text",
        label: "City",
        required: true
      },
      {
        id: "province",
        type: "select",
        label: "Province or Territory",
        options: [
          { value: "AB", label: "Alberta" },
          { value: "BC", label: "British Columbia" },
          { value: "MB", label: "Manitoba" },
          { value: "NB", label: "New Brunswick" },
          { value: "NL", label: "Newfoundland and Labrador" },
          { value: "NT", label: "Northwest Territories" },
          { value: "NS", label: "Nova Scotia" },
          { value: "NU", label: "Nunavut" },
          { value: "ON", label: "Ontario" },
          { value: "PE", label: "Prince Edward Island" },
          { value: "QC", label: "Quebec" },
          { value: "SK", label: "Saskatchewan" },
          { value: "YT", label: "Yukon" }
        ],
        required: true
      },
      {
        id: "postal_code",
        type: "text",
        label: "Postal Code",
        hint: "Format: A1A 1A1",
        required: true,
        validation: {
          pattern: "^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$",
          errorMessage: "Enter a valid Canadian postal code (e.g., K1A 0B1)."
        }
      },
      {
        id: "mailing_address",
        type: "textarea",
        label: "Mailing Address (if different)",
        hint: "Leave blank if your mailing address is the same as your street address.",
        required: false
      },
      {
        id: "daytime_phone",
        type: "tel",
        label: "Daytime Phone Number",
        hint: "Include area code (e.g., 613-555-1234).",
        required: true
      },
      {
        id: "alternate_phone",
        type: "tel",
        label: "Alternate Phone Number",
        hint: "Optional. Include area code.",
        required: false
      },
      {
        id: "email",
        type: "email",
        label: "Email Address",
        hint: "We will use this to contact you about your application.",
        required: true,
        validation: {
          pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
          errorMessage: "Enter a valid email address."
        }
      }
    ]
  },
  {
    stepId: "emergency_contact",
    title: "Emergency Contact",
    description: "Provide someone we can contact in case of an emergency or if we're unable to reach you directly.",
    questions: [
      {
        id: "emergency_contact_name",
        type: "text",
        label: "Emergency Contact Name",
        required: true
      },
      {
        id: "emergency_contact_phone",
        type: "tel",
        label: "Emergency Contact Phone Number",
        hint: "Include area code (e.g., 613-555-9876).",
        required: true,
        validation: {
          pattern: "^\\d{3}-\\d{3}-\\d{4}$",
          errorMessage: "Enter a valid phone number (e.g., 613-555-9876)."
        }
      },
      {
        id: "emergency_contact_relationship",
        type: "text",
        label: "Relationship to You",
        hint: "For example: Parent, Friend, Caseworker, Partner",
        required: true
      }
    ]
  },
  {
    stepId: "indigenous_group",
    title: "Which Indigenous group(s) do you belong to?",
    questions: [
      {
        id: "indigenous_group",
        type: "checkbox",
        label: "Select your indigenous group:",
        options: [
          { value: "status_first_nation", label: "Status First Nation" },
          { value: "non_status_first_nation", label: "Non-status First Nation" },
          { value: "metis", label: "Métis" },
          { value: "inuit", label: "Inuit" }
        ],
        required: true
      }
    ]
  },
  {
    stepId: "indigenous_registration_number",
    title: "What is your registration number?",
    questions: [
      {
        id: "indigenous_registration_number",
        type: "text",
        label: "Registration Number (if applicable)",
        hint: 'If you do not have a registration number, enter "N/A".',
        required: true,
        validation: {
          maxLength: 32,
          errorMessage: "Registration number must be 32 characters or less. Enter 'N/A' if not applicable."
        }
      }
    ]
  },
  {
    stepId: "indigenous_home_community",
    title: "What is your home community?",
    questions: [
      {
        id: "indigenous_home_community",
        type: "text",
        label: "Home Community",
        hint: "For example: your First Nation, Métis, or Inuit community.",
        required: true,
        validation: {
          maxLength: 64,
          errorMessage: "Home community must be 64 characters or less."
        }
      }
    ]
  },
  {
    stepId: "demographics",
    title: "Demographics",
    description: "This information helps us understand your circumstances and ensure equitable access to services.",
    questions: [
      {
        id: "visible_minority",
        type: "radio",
        label: "Do you identify as a member of a visible minority group (not including Indigenous identity)?",
        hint: "This question helps us understand diversity and ensure inclusive programming.",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" }
        ],
        required: true
      },
      {
        id: "preferred_language",
        type: "select",
        label: "Preferred Language",
        hint: "Select the language you prefer for all program-related communication.",
        options: [
          { value: "english", label: "English" },
          { value: "french", label: "French" },
          { value: "other", label: "Other" }
        ],
        required: true
      },
      {
        id: "marital_status",
        type: "radio",
        label: "Marital Status",
        hint: "We ask about your relationship status to help assess household responsibilities.",
        options: [
          { value: "married", label: "Married or Equivalent" },
          { value: "single", label: "Single" },
          { value: "separated", label: "Separated" },
          { value: "divorced", label: "Divorced" },
          { value: "widowed", label: "Widowed" }
        ],
        required: true
      },
      {
        id: "spouse_name",
        type: "text",
        label: "Spouse's Name",
        hint: "Provide the name of your spouse or partner if you're married or in an equivalent relationship.",
        required: false,
        conditional: {
          field: "marital_status",
          value: "married"
        }
      },
      {
        id: "has_dependents",
        type: "radio",
        label: "Do you have dependent children?",
        hint: "Select 'Yes' if you are responsible for children who depend on your care.",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" }
        ],
        required: true
      },
      {
        id: "children_ages",
        type: "text",
        label: "Ages of Children",
        hint: "List the ages of your children separated by commas (e.g., 4, 7, 13).",
        required: false,
        conditional: {
          field: "has_dependents",
          value: "yes"
        }
      }
    ]
  }
,
  {
    stepId: "disability_social_assistance",
    title: "Disability and Social Assistance",
    description: "This section helps us understand any additional supports you may require.",
    questions: [
      {
        id: "has_disability",
        type: "radio",
        label: "Do you consider yourself to have a disability?",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" }
        ],
        required: true
      },
      {
        id: "disability_description",
        type: "textarea",
        label: "Please specify your disability",
        hint: "Optional. Only required if you answered 'Yes' to the previous question.",
        required: false,
        conditional: {
          field: "has_disability",
          value: "yes"
        }
      },
      {
        id: "receives_social_assistance",
        type: "radio",
        label: "Are you currently receiving social assistance?",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" }
        ],
        required: true
      },
      {
        id: "social_assistance_topup",
        type: "text",
        label: "If yes, what is your allowable top-up amount (if applicable)?",
        hint: "Enter the dollar amount. Leave blank if unsure.",
        required: false,
        conditional: {
          field: "receives_social_assistance",
          value: "yes"
        }
      }
    ]
  },
  {
    stepId: "labour_education",
    title: "Labour Force and Education History",
    description: "Tell us about your current employment situation and your educational background.",
    questions: [
      {
        id: "labour_force_status",
        type: "radio",
        label: "What is your current labour force status?",
        options: [
          { value: "unemployed", label: "Unemployed" },
          { value: "underemployed", label: "Under-employed" },
          { value: "employed_full_time", label: "Employed Full-time" },
          { value: "employed_part_time", label: "Employed Part-time" },
          { value: "self_employed", label: "Self-employed" },
          { value: "student", label: "Student" },
          { value: "other", label: "Other" }
        ],
        required: true
      },
      {
        id: "education_level",
        type: "checkbox",
        label: "What education levels have you completed?",
        hint: "Select all that apply.",
        options: [
          { value: "none", label: "No formal education" },
          { value: "grade_7_8", label: "Up to Grade 7–8 (Secondaire I–II)" },
          { value: "grade_9_10", label: "Grade 9–10 (Secondaire III)" },
          { value: "grade_11_12", label: "Grade 11–12 (Secondaire IV–V)" },
          { value: "high_school", label: "Secondary School Diploma or GED" },
          { value: "some_post_secondary", label: "Some post-secondary training" },
          { value: "apprenticeship", label: "Apprenticeship/trades certificate or diploma" },
          { value: "cegep", label: "CEGEP or other non-university certificate/diploma" },
          { value: "college", label: "College or other non-university certificate/diploma" },
          { value: "university_certificate", label: "University certificate or diploma" },
          { value: "bachelor", label: "University – Bachelor Degree" },
          { value: "masters", label: "University – Master’s Degree" },
          { value: "doctorate", label: "University – Doctorate" }
        ],
        required: true,
        validation: {
          atLeastOne: true,
          errorMessage: "Select at least one education level."
        }
      },
      {
        id: "education_year_completed",
        type: "text",
        label: "Year you completed your highest level of education",
        hint: "Enter the year (e.g., 2018). Leave blank if unknown.",
        required: false,
        validation: {
          pattern: "^(19|20)\\d{2}$",
          errorMessage: "Enter a valid 4-digit year (e.g., 2020)."
        }
      },
      {
        id: "education_location",
        type: "text",
        label: "Province or Territory where you completed your highest level of education",
        required: false
      }
    ]
  },
  {
    stepId: "employment_goals",
    title: "Employment Goals and Barriers",
    description: "Help us understand your career goals and any challenges you're facing.",
    questions: [
      {
        id: "employment_goals",
        type: "textarea",
        label: "What is your long-term employment goal?",
        hint: "Describe the type of job or career you hope to achieve.",
        required: true
      },
      {
        id: "employment_barriers",
        type: "checkbox",
        label: "What barriers are you currently facing?",
        hint: "Select all that apply.",
        options: [
          { value: "education", label: "Education" },
          { value: "funding", label: "Funding" },
          { value: "job_opportunities", label: "Lack of Job Opportunities" },
          { value: "location", label: "Location (e.g., remote community)" },
          { value: "other", label: "Other" }
        ],
        required: false
      },
      {
        id: "barriers_other_text",
        type: "text",
        label: "Other barrier (please specify)",
        required: false,
        conditional: {
          field: "employment_barriers",
          value: "other"
        }
      },
      {
        id: "identified_path",
        type: "radio",
        label: "Have you already identified a training program or employer?",
        options: [
          { value: "skills_development", label: "Skills Development (Education)" },
          { value: "wage_subsidy", label: "Targeted Wage Subsidy" },
          { value: "job_creation", label: "Job Creation Partnership" },
          { value: "none", label: "Not yet" }
        ],
        required: true
      }
    ]
  },
  {
    stepId: "financial_supports",
    title: "Financial Supports Requested",
    description: "Tell us what financial support you are requesting to help you reach your employment goal.",
    questions: [
      {
        id: "financial_support_types",
        type: "checkbox",
        label: "Which of the following supports are you requesting?",
        hint: "Select all that apply.",
        options: [
          { value: "tuition", label: "Tuition" },
          { value: "books", label: "Books or Program Materials" },
          { value: "living_allowance", label: "Living Allowance" },
          { value: "transportation", label: "Transportation" },
          { value: "other", label: "Other" }
        ],
        required: true,
        validation: {
          atLeastOne: true,
          errorMessage: "Please select at least one type of support."
        }
      },
      {
        id: "support_other_detail",
        type: "text",
        label: "Other support (please specify)",
        required: false,
        conditional: {
          field: "financial_support_types",
          value: "other"
        }
      },
      {
        id: "childcare_requested",
        type: "radio",
        label: "Are you requesting a childcare subsidy?",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" }
        ],
        required: true
      },
      {
        id: "childcare_funding_source",
        type: "select",
        label: "What childcare funding or support are you currently receiving?",
        hint: "Choose the applicable source. Select 'Not applicable' if you are not receiving childcare support.",
        required: false,
        options: [
          { value: "not_applicable", label: "Not applicable" },
          { value: "ei_crf", label: "EI/CRF" },
          { value: "fnicci", label: "FNICCI" },
          { value: "provincial", label: "Provincial funding/subsidy" },
          { value: "no_funding", label: "No funding received" },
          { value: "daycare_not_available", label: "Daycare not available" },
          { value: "assisted_by_family", label: "Assisted by family" },
          { value: "self_funded", label: "Self-funded" }
        ],
        conditional: {
          field: "childcare_requested",
          value: "yes"
        }
      }
    ]
  },
  {
    stepId: "other_funding",
    title: "Other Funding Sources",
    description: "Let us know if you are receiving any other funding (e.g., band funding, student loans, or grants).",
    questions: [
      {
        id: "receives_other_funding",
        type: "radio",
        label: "Are you currently receiving any other funding?",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" }
        ],
        required: true
      },
      {
        id: "other_funding_details",
        type: "text",
        label: "If yes, please specify the program and amount",
        hint: "For example: Band funding – $2,000",
        required: false,
        conditional: {
          field: "receives_other_funding",
          value: "yes"
        }
      }
    ]
  },
  {
    stepId: "household_income",
    title: "Household Income",
    description: "Please disclose all sources of monthly household income. Documentation and proof of financial situation is required prior to any approval of funding.",
    questions: [
      {
        id: "employment_income",
        type: "number",
        label: "Employment Income ($)",
        required: false,
        min: 0
      },
      {
        id: "spousal_income",
        type: "number",
        label: "Spousal Income ($)",
        required: false,
        min: 0
      },
      {
        id: "social_assistance",
        type: "number",
        label: "Social Assistance ($)",
        required: false,
        min: 0
      },
      {
        id: "child_tax_benefit",
        type: "number",
        label: "Child Tax Benefit ($)",
        required: false,
        min: 0
      },
      {
        id: "jordans_principle",
        type: "number",
        label: "Jordan’s Principle ($)",
        required: false,
        min: 0
      },
      {
        id: "band_funding",
        type: "number",
        label: "Band Funding ($)",
        required: false,
        min: 0
      },
      {
        id: "other_income_desc",
        type: "text",
        label: "Other (please specify)",
        required: false
      },
      {
        id: "other_income_amount",
        type: "number",
        label: "Other Income Amount ($)",
        required: false,
        min: 0
      }
    ],
    validation: {
      atLeastOne: [
        "employment_income",
        "spousal_income",
        "social_assistance",
        "child_tax_benefit",
        "jordans_principle",
        "band_funding",
        "other_income_amount"
      ],
      errorMessage: "Please enter at least one source of income."
    }
  },
  {
    stepId: "household_expenses",
    title: "Household Expenses",
    description: "Please disclose all monthly household expenses. This information is required to assess your eligibility for a living allowance.",
    questions: [
      {
        id: "rent_mortgage",
        type: "number",
        label: "Rent/Mortgage ($)",
        required: false,
        min: 0
      },
      {
        id: "utilities",
        type: "number",
        label: "Utilities (Hydro, water, natural gas, sewer, garbage) ($)",
        required: false,
        min: 0
      },
      {
        id: "groceries",
        type: "number",
        label: "Groceries ($)",
        required: false,
        min: 0
      },
      {
        id: "transit_pass",
        type: "number",
        label: "Transit Pass ($)",
        required: false,
        min: 0
      },
      {
        id: "childcare",
        type: "number",
        label: "Childcare* ($)",
        required: false,
        min: 0
      },
      {
        id: "other_expenses_desc",
        type: "text",
        label: "Other (please specify)",
        required: false
      },
      {
        id: "other_expenses_amount",
        type: "number",
        label: "Other Expenses Amount ($)",
        required: false,
        min: 0
      }
    ],
    validation: {
      atLeastOne: [
        "rent_mortgage",
        "utilities",
        "groceries",
        "transit_pass",
        "childcare",
        "other_expenses_amount"
      ],
      errorMessage: "Please enter at least one expense."
    }
  }
];
